import React, { useState } from 'react';
import { AlertTriangle, Shield, Calendar, Mail, Info } from 'lucide-react';
import { useForm, SubmitHandler } from "react-hook-form";
import { requestAccountDeletion, cancelAccountDeletion } from '../../utils/user';
import useTokenStore from '../../store/store';
import useThemeStore from '../../store/themeStore';
import { ThemeProps } from '../../utils/theme';

interface AccountDeletionSectionProps {
  profile: {
    email: string;
    deletionRequested: boolean;
    deletionScheduledFor?: string | null;
    deletionCancellationToken?: string | null;
    accountStatus: 'active' | 'pending_deletion' | 'deleted';
  };
  onDeletionUpdate: () => void; // Callback to refresh profile data
  isOpen?: boolean;
  onToggleOpen?: (open: boolean) => void;
}

type DeletionFormInputs = {
  confirmEmail: string;
  reason: string;
  confirmDelete: boolean;
};

const AccountDeletionSection: React.FC<AccountDeletionSectionProps> = ({ 
  profile, 
  onDeletionUpdate,
  isOpen: externalIsOpen,
  onToggleOpen
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggleOpen || setInternalIsOpen;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');

  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<DeletionFormInputs>();

  const confirmDelete = watch('confirmDelete', false);

  // White color detection function
  const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
    
    if (s === "white" || s === "#ffffff" || s === "#fff" || s === "rgb(255, 255, 255)" || s === "rgba(255, 255, 255, 1)") {
      return true;
    }
    
    if (s.startsWith("#") && s.length === 7) {
      const hex = s.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 240;
    }
    
    if (s.startsWith("rgb")) {
      const matches = s.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 240;
      }
    }
    
    return false;
  };

  const getContrastColor = (backgroundColor: string): string => {
    return isWhiteOrShade(backgroundColor) ? "#1f2937" : "#ffffff";
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (deletionDate: string): number => {
    const today = new Date();
    const deletion = new Date(deletionDate);
    const diffTime = deletion.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const onSubmitDeletion: SubmitHandler<DeletionFormInputs> = async (data) => {
    if (!getToken) {
      setMessage('Authentication token not found. Please log in again.');
      setMessageType('error');
      return;
    }

    setIsDeleting(true);
    setMessage('');

    try {
      const result = await requestAccountDeletion(getToken, {
        confirmEmail: data.confirmEmail,
        reason: data.reason
      });

      if (result.status === 200) {
        setMessage('Account deletion has been scheduled successfully. Check your email for confirmation details.');
        setMessageType('success');
        setShowDeleteForm(false);
        reset();
        onDeletionUpdate(); // Refresh profile data
      } else {
        setMessage(result.message || 'Failed to request account deletion');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('An error occurred while requesting account deletion');
      setMessageType('error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!getToken || !profile.deletionCancellationToken) {
      setMessage('Unable to cancel deletion. Please contact support.');
      setMessageType('error');
      return;
    }

    setIsCancelling(true);
    setMessage('');

    try {
      const result = await cancelAccountDeletion(getToken, profile.deletionCancellationToken);

      if (result.status === 200) {
        setMessage('Account deletion has been cancelled successfully. Your account is now active.');
        setMessageType('success');
        onDeletionUpdate(); // Refresh profile data
      } else {
        setMessage(result.message || 'Failed to cancel account deletion');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('An error occurred while cancelling account deletion');
      setMessageType('error');
    } finally {
      setIsCancelling(false);
    }
  };

  // If account deletion is pending, show cancellation interface
  if (profile.accountStatus === 'pending_deletion' && profile.deletionScheduledFor) {
    const daysRemaining = getDaysRemaining(profile.deletionScheduledFor);
    
    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-red-200 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 ease-in-out">
        {/* Warning Header */}
        <div 
          className="bg-red-50 px-6 py-4 border-b border-red-200 cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-bold text-red-800">
                Account Deletion Scheduled
              </h3>
            </div>
            <div
              className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              style={{ color: '#dc2626' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-red-600">
              Your account will be permanently deleted in {daysRemaining} days
            </p>
          </div>
        </div>

        {/* Collapsible Content */}
        {isOpen && (
          <div className="p-6 space-y-4">
          {/* Message Display */}
          {message && (
            <div className={`p-4 rounded-lg ${
              messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message}
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-center mb-2">
              <Info className="w-5 h-5 text-yellow-600 mr-3" />
              <h4 className="font-semibold text-yellow-800">Deletion Details</h4>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-center text-sm text-gray-700">
                <Calendar className="w-4 h-4 mr-2" />
                <span>Scheduled for deletion: {formatDate(profile.deletionScheduledFor)}</span>
              </div>
              <div className="flex items-center justify-center text-sm text-gray-700">
                <Mail className="w-4 h-4 mr-2" />
                <span>Confirmation email sent to: {profile.email}</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">What happens when my account is deleted?</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Your personal information will be permanently removed</li>
              <li>• Your favorite properties will be deleted</li>
              <li>• Your login credentials will be removed</li>
              <li>• Some transaction records may be retained for regulatory compliance</li>
              <li>• This action cannot be undone after the deletion date</li>
            </ul>
          </div>

          {/* Cancel Deletion Button */}
          <div className="flex justify-center pt-4">
            <button
              onClick={handleCancelDeletion}
              disabled={isCancelling}
              className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md disabled:opacity-50"
              style={{
                backgroundColor: getThemes?.color1 || "#0055cc",
                color: getContrastColor(getThemes?.color1 || "#0055cc"),
              }}
              onMouseEnter={(e) => {
                if (!isCancelling) {
                  e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                  e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                }
              }}
              onMouseLeave={(e) => {
                if (!isCancelling) {
                  e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                  e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                }
              }}
            >
              {isCancelling ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                  Cancelling...
                </>
              ) : (
                'Cancel Account Deletion'
              )}
            </button>
          </div>
          </div>
        )}
      </div>
    );
  }

  // If account is active, show deletion request interface
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 ease-in-out">
      {/* Header */}
      <div
        className="px-6 py-4 cursor-pointer hover:opacity-90 transition-opacity"
        style={{ background: getThemes?.color1 }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="w-6 h-6 mr-3" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
            <h3 
              className="text-xl font-bold"
              style={{ color: getContrastColor(getThemes?.color1 || "") }}
            >
              Account Deletion
            </h3>
          </div>
          <div
            className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: getContrastColor(getThemes?.color1 || "") }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Collapsible Content */}
      {isOpen && (
        <div className="p-6">
        {/* Message Display */}
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {message}
          </div>
        )}

        {!showDeleteForm ? (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-center mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
                <h4 className="font-semibold text-red-800">Permanently Delete Account</h4>
              </div>
              <div>
                <p className="text-sm text-red-700 mb-3 text-center">
                  This action will permanently delete your account and all associated data. 
                  You will have 30 days to cancel this request.
                </p>
                <ul className="text-xs text-red-600 space-y-1 mb-4">
                  <li>• All personal information will be removed</li>
                  <li>• Your favorite properties will be deleted</li>
                  <li>• You cannot recover your account after the deletion date</li>
                  <li>• Some transaction records may be retained for compliance</li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowDeleteForm(true)}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: getThemes?.color1 || "#0055cc",
                  color: getContrastColor(getThemes?.color1 || "#0055cc"),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                  e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                  e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                }}
              >
                Request Account Deletion
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmitDeletion)} className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3" />
                <h4 className="font-semibold text-yellow-800">Important Information</h4>
              </div>
              <div className="text-center">
                <p className="text-sm text-yellow-700">
                  Please read carefully before proceeding. This action will schedule your account for permanent deletion in 30 days.
                </p>
              </div>
            </div>

            {/* Email Confirmation */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Confirm your email address *
              </label>
              <input
                {...register("confirmEmail", { 
                  required: "Email confirmation is required",
                  validate: value => value === profile.email || "Email does not match your account email"
                })}
                type="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900 bg-white"
                placeholder={`Enter ${profile.email} to confirm`}
              />
              {errors.confirmEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmEmail.message}</p>
              )}
            </div>

            {/* Reason (Optional) */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Reason for deletion (optional)
              </label>
              <textarea
                {...register("reason")}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900 bg-white"
                rows={3}
                placeholder="Help us improve by telling us why you're leaving..."
              />
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-start">
              <input
                {...register("confirmDelete", { 
                  required: "You must confirm to proceed with deletion" 
                })}
                type="checkbox"
                className="mt-1 mr-3 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label className="text-sm text-gray-700">
                I understand that this action will permanently delete my account and all data after 30 days, 
                and that this cannot be undone after the deletion date.
              </label>
            </div>
            {errors.confirmDelete && (
              <p className="text-sm text-red-600">{errors.confirmDelete.message}</p>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteForm(false);
                  reset();
                  setMessage('');
                }}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: getThemes?.color1 || "#0055cc",
                  color: getContrastColor(getThemes?.color1 || "#0055cc"),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                  e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                  e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isDeleting || !confirmDelete}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: getThemes?.color1 || "#0055cc",
                  color: getContrastColor(getThemes?.color1 || "#0055cc"),
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting && confirmDelete) {
                    e.currentTarget.style.backgroundColor = getThemes?.color2 || "#0066dd";
                    e.currentTarget.style.color = getContrastColor(getThemes?.color2 || "#0066dd");
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting && confirmDelete) {
                    e.currentTarget.style.backgroundColor = getThemes?.color1 || "#0055cc";
                    e.currentTarget.style.color = getContrastColor(getThemes?.color1 || "#0055cc");
                  }
                }}
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Schedule Account Deletion'
                )}
              </button>
            </div>
          </form>
        )}
        </div>
      )}
    </div>
  );
};

export default AccountDeletionSection;